import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { SelectWidgetWrapper } from '@framework/engine/schema/widgets/repository/inputs/common/select-widget/SelectWidgetWrapper.tsx';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { useField } from '@framework/form/field/useField.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import {
  getProvinceDataFieldName,
  getProvinceFieldName,
  getTownDataFieldName,
  getTownFieldName,
} from '../createProvinceWidgetSchema.ts';
import { type IProvinceWidget } from '../IProvinceWidget.ts';
import {
  type IProvinceItem,
  type ITownItem,
} from '../utils/mapProvinceList.ts';

export interface IProvinceWidgetSelectComponentProps
  extends IWidgetComponentProps<IProvinceWidget> {
  provinces?: IProvinceItem[];
  towns?: ITownItem[];
  loading?: boolean;
}

/**
 * Componente generale che gestisce le select di provincia e comune.
 * Utilizzato "senza parametri" come preview del widget.
 */
export function ProvinceWidgetSelectComponent(
  props: IProvinceWidgetSelectComponentProps,
) {
  const { widget, loading, provinces, towns } = props;
  const widgetOptions = getWidgetProps(props);

  const placeholders = useMemo(() => {
    if (props.componentLayout?.hidePlaceholder) {
      return { town: '', province: '' };
    }
    const { townPlaceholder, provincePlaceholder } = widget;

    return { town: townPlaceholder ?? '', province: provincePlaceholder ?? '' };
  }, [props.componentLayout?.hidePlaceholder, widget]);

  const provinceFieldName = getProvinceFieldName(widgetOptions.name);
  const provinceDataFieldName = getProvinceDataFieldName(widgetOptions.name);
  const townFieldName = getTownFieldName(widgetOptions.name);
  const townDataFieldName = getTownDataFieldName(widgetOptions.name);

  const provinceDataField = useField(provinceDataFieldName);
  const selectedProvince = useWatch({
    name: provinceFieldName,
  });

  const townDataField = useField(townDataFieldName);

  // Mostro solo i comuni della provincia selezionata
  const filteredTowns = useMemo(() => {
    if (!selectedProvince || !towns) return [];
    return towns.filter(town => town.context?.provincia === selectedProvince);
  }, [selectedProvince, towns]);

  return (
    <RowElement>
      <SelectWidgetWrapper
        {...props}
        name={provinceFieldName}
        formItemName={provinceFieldName}
        label={widget.provinceLabel}
        helpText={widget.provinceHelpText}
      >
        <SelectInput
          name={provinceFieldName}
          readOnly={props.readonly}
          options={provinces ?? []}
          loading={loading}
          optionFilterProp="label"
          allowClear
          placeholder={placeholders.province}
          // per accessibilità limitiamo virtual a 20 opzioni
          showSearch={true}
          onChange={(value, option: any, onChange) => {
            provinceDataField.setValue(option?.context ?? null);
            onChange(value);
          }}
        />
      </SelectWidgetWrapper>
      <SelectWidgetWrapper
        {...props}
        name={townFieldName}
        formItemName={townFieldName}
        label={widget.townLabel}
        helpText={widget.townHelpText}
      >
        <SelectInput
          disabled={!selectedProvince && !props.preview}
          name={townFieldName}
          readOnly={props.readonly}
          options={filteredTowns ?? []}
          loading={loading}
          optionFilterProp="label"
          allowClear
          placeholder={placeholders.town}
          // per accessibilità limitiamo virtual a 20 opzioni
          onChange={(value, option: any, onChange) => {
            townDataField.setValue(option?.context ?? null);
            onChange(value);
          }}
          showSearch={true}
        />
      </SelectWidgetWrapper>
    </RowElement>
  );
}
